import { useState } from 'react';
import { Download } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { casaFacilProductFromName } from '../lib/casaFacilProduct';

/**
 * Reads the download name sent by the API.
 */
function fileNameFromDisposition(header, fallback) {
  const match = /filename="([^"]+)"/.exec(header || '');
  return match ? match[1] : fallback;
}

/**
 * Reads an error message from a blob response.
 */
async function downloadErrorMessage(error, fallback) {
  const payload = error.response?.data;
  if (payload instanceof Blob) {
    try {
      return JSON.parse(await payload.text()).error || fallback;
    } catch (parseError) {
      return fallback;
    }
  }
  return error.response?.data?.error || fallback;
}

/**
 * Downloads the conference Excel or the invoice PDF of one Casa Fácil file.
 */
async function downloadReport(fileId, fileName, kind) {
  const isPdf = kind === 'pdf';
  const response = await api.get(`/upload/files/${fileId}/${isPdf ? 'invoice' : 'excel'}`, {
    responseType: 'blob'
  });
  const blob = new Blob([response.data], {
    type: isPdf
      ? 'application/pdf'
      : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  const baseName = String(fileName || 'CASA_FACIL').replace(/\.txt$/i, '');
  link.href = url;
  link.download = fileNameFromDisposition(
    response.headers['content-disposition'],
    isPdf ? 'FATURA_CASA_FACIL.pdf' : `${baseName}.xlsx`
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Shows the Excel and invoice actions for a Casa Fácil Resolve or Resolve Mais file.
 */
export default function CasaFacilReportButtons({ fileId, fileName }) {
  const [downloading, setDownloading] = useState(null);
  if (!fileId || !casaFacilProductFromName(fileName)) return null;

  const startDownload = async (kind) => {
    try {
      setDownloading(kind);
      await downloadReport(fileId, fileName, kind);
    } catch (error) {
      toast.error(await downloadErrorMessage(error, kind === 'pdf' ? 'Erro ao gerar a fatura' : 'Erro ao gerar o Excel'));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => startDownload('excel')}
        disabled={downloading === 'excel'}
        className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-primary-700 bg-primary-50 hover:bg-primary-100 disabled:opacity-60"
      >
        <Download className="h-3 w-3 mr-1" />
        {downloading === 'excel' ? 'Gerando...' : 'Excel'}
      </button>
      <button
        type="button"
        onClick={() => startDownload('pdf')}
        disabled={downloading === 'pdf'}
        className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-60"
      >
        <Download className="h-3 w-3 mr-1" />
        {downloading === 'pdf' ? 'Gerando...' : 'Fatura'}
      </button>
    </>
  );
}
